import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  FileText,
  ClipboardList,
  Loader2,
  ChevronRight,
  CheckCircle,
  Clock,
  Home
} from 'lucide-react';
import { format } from 'date-fns';

const MobileForms = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, operator } = useMobileAuth();
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'available');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [templatesRes, submissionsRes] = await Promise.all([
          api.get('/api/form-templates'),
          api.get('/api/form-submissions')
        ]);
        
        // Filter only standalone templates that are active
        const standaloneTemplates = templatesRes.data.filter(
          t => t.is_active && t.form_type === 'standalone'
        );
        setTemplates(standaloneTemplates);
        
        // Filter submissions by this operator (standalone forms only)
        const mySubmissions = submissionsRes.data.filter(
          s => s.operator_id === operator?.id && !s.job_id
        );
        setSubmissions(mySubmissions);
      } catch (error) {
        console.error('Failed to fetch forms data:', error);
        toast.error('Failed to load forms');
      } finally {
        setLoading(false);
      }
    };

    if (operator) {
      fetchData();
    }
  }, [api, operator]);

  const handleStartForm = (template) => {
    navigate(`/mobile/forms/submit?templateId=${template.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/mobile/dashboard')} className="text-gray-400 p-1">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[#ffd108]" />
            <h1 className="text-white font-semibold">Forms</h1>
          </div>
        </div>
        <button 
          onClick={() => navigate('/mobile/dashboard')} 
          className="text-gray-400 p-1"
          data-testid="home-btn"
        >
          <Home className="h-5 w-5" />
        </button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-[#2a2a2a] rounded-none border-b border-gray-700">
          <TabsTrigger 
            value="available" 
            className="flex-1 data-[state=active]:bg-[#ffd108] data-[state=active]:text-black"
          >
            Available
          </TabsTrigger>
          <TabsTrigger 
            value="submitted" 
            className="flex-1 data-[state=active]:bg-[#ffd108] data-[state=active]:text-black"
          >
            Submitted ({submissions.length})
          </TabsTrigger>
        </TabsList>

        {/* Available Forms Tab - Standalone Forms Only */}
        <TabsContent value="available" className="p-4">
          {templates.length > 0 ? (
            <div className="space-y-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleStartForm(template)}
                  className="w-full p-4 bg-[#2a2a2a] rounded-lg text-left hover:bg-[#3a3a3a] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-medium">{template.name}</h3>
                      {template.description && (
                        <p className="text-sm text-gray-400 mt-1">{template.description}</p>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No standalone forms available</p>
              <p className="text-sm mt-1">Job-related forms can be accessed from Job Details</p>
            </div>
          )}
        </TabsContent>

        {/* Submitted Forms Tab */}
        <TabsContent value="submitted" className="p-4">
          {submissions.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No submissions yet</p>
              <p className="text-sm mt-1">Your completed forms will appear here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {submissions.map((submission) => (
                <div
                  key={submission.id}
                  className="p-4 bg-[#2a2a2a] rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-white font-medium">{submission.template_name}</h3>
                      {submission.job_description && (
                        <p className="text-sm text-gray-400 mt-1">
                          Job: {submission.job_description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <Badge 
                          className={submission.status === 'submitted' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                          }
                        >
                          {submission.status === 'submitted' ? (
                            <><CheckCircle className="h-3 w-3 mr-1" /> Submitted</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> Draft</>
                          )}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {format(new Date(submission.submitted_at || submission.created_at), 'dd/MM/yyyy HH:mm')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MobileForms;
